import React from 'react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { MapPin, Phone, Clock, MessageCircle, Zap, Users, Building2 } from 'lucide-react';

const Areas = () => {
  const areas = [
    { name: 'Central London', desc: 'Westminster, Kensington, Chelsea, Fitzrovia and surrounding areas.', image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800' },
    { name: 'Chiswick', desc: 'Professional HVAC services in Chiswick and West London areas.', image: '/images/areas/chiswick.jpg' },
    { name: "Shepherd's Bush", desc: "Covering Shepherd's Bush and surrounding West London areas.", image: '/images/areas/shepherds-bush.jpg' },
    { name: 'Tower Hamlets', desc: 'Expert HVAC services across Tower Hamlets and East London.', image: 'https://images.unsplash.com/photo-1520986606214-8b456906c813?w=800' },
    { name: 'Canary Wharf', desc: 'Commercial & residential HVAC services in Canary Wharf and the Docklands.', image: '/images/areas/canary-wharf.jpg' },
    { name: 'South London', desc: 'Serving Wimbledon, Croydon, Bromley and surrounding South London areas.', image: '/images/areas/south-london.jpg' },
    { name: 'North London', desc: 'Covering Camden, Islington, Haringey and surrounding North London areas.', image: 'https://images.unsplash.com/photo-1526129318478-62ed807ebdf9?w=800' },
    { name: 'Surrey', desc: 'Providing HVAC services across Surrey and surrounding towns and villages.', image: '/images/areas/surrey.jpg' },
  ];

  const benefits = [
    { icon: MapPin, title: 'London Wide Coverage', desc: 'Covering all areas of London & surrounding regions' },
    { icon: Zap, title: 'Fast Response Times', desc: 'Same-day service available in most areas' },
    { icon: Users, title: 'Local HVAC Experts', desc: 'Professional engineers who know your area' },
    { icon: Clock, title: '24/7 Emergency Call-Outs', desc: 'Available for urgent repairs and breakdowns' },
    { icon: Building2, title: 'Commercial & Domestic', desc: 'Services for homes and businesses' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0E14]">
      <SEO
        title="Areas We Cover Across London & Surrey"
        description="Deep Control HVAC services Central London, Canary Wharf, Chiswick, Shepherd's Bush, South London, Tower Hamlets, North London and Surrey. Same-day response in most areas."
        path="/areas"
      />
      {/* Hero Section with Network Video */}
      <section className="relative min-h-[65vh] flex items-center bg-[#0A0E14]">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="/videos/areas-network-poster.jpg"
            className="w-full h-full object-cover opacity-40"
            data-testid="areas-hero-video"
          >
            <source src="/videos/areas-network.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E14]/55 to-[#0A0E14]/75" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full text-center z-10">
          <span className="inline-block text-[10px] bg-white/5 px-4 py-2 rounded-full text-[#FF8C00] font-bold border border-[#FF8C00]/20 tracking-[2px] backdrop-blur-sm mb-6">
            AREAS WE COVER
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight tracking-tight mb-6">
            Proudly Serving <span className="text-[#FF8C00]">London</span> <br />& Surrounding Areas
          </h1>
          <p className="text-gray-300 text-xl leading-relaxed max-w-3xl mx-auto">
            We provide professional HVAC services across London and the surrounding areas. No matter where you are, our team is ready to help.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#0A0E14] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF8C00]/10 flex items-center justify-center">
                    <Icon size={28} className="text-[#FF8C00]" />
                  </div>
                  <h3 className="text-base font-bold mb-2 leading-tight">{benefit.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{benefit.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Areas Grid with City Photos */}
      <section className="py-24 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-[11px] tracking-[3px] uppercase mb-3">COVERAGE AREAS</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">Areas We Cover</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {areas.map((area, idx) => (
              <div key={idx} className="group relative overflow-hidden rounded-2xl h-64">
                {/* City Photo Background */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{
                    backgroundImage: `url('${area.image}')`
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E14] via-[#0A0E14]/60 to-transparent" />
                
                {/* Orange Location Pin */}
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#FF8C00] flex items-center justify-center shadow-lg shadow-[#FF8C00]/40 animate-pulse">
                  <MapPin size={20} className="text-white" />
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold mb-2 group-hover:text-[#FF8C00] transition-colors">{area.name}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{area.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Not Sure Section */}
      <section className="py-24 bg-[#121820]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-[#0A0E14] border border-white/5 rounded-2xl p-10 md:p-14 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#FF8C00]/10 flex items-center justify-center">
              <MapPin size={40} className="text-[#FF8C00]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">Not Sure If We Cover Your Area?</h2>
            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Get in touch with our team and we'll be happy to confirm if we can help with your HVAC needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:02080599941">
                <Button className="bg-[#FF8C00] hover:bg-[#e67e00] text-white font-bold px-8 py-6 rounded-lg text-base w-full sm:w-auto">
                  <Phone size={20} className="mr-2" /> Call 020 8059 9941
                </Button>
              </a>
              <a href="https://wa.me/447990547352" target="_blank" rel="noopener noreferrer">
                <Button className="border-2 border-white/30 hover:bg-white/10 text-white font-bold px-8 py-6 rounded-lg text-base w-full sm:w-auto">
                  <MessageCircle size={20} className="mr-2" /> WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Areas;
